import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import {
  Send,
  Sparkles,
  Plus,
  LogOut,
  Rocket,
  Code2,
  Eye,
  FileCode2,
  MonitorSmartphone,
  Globe2,
  Layers,
  Wand2,
  ChevronRight,
  ChevronDown,
  Copy,
  Loader2,
  Paperclip,
  GitBranch,
  Mic,
  Cpu,
  UserRound,
  Smartphone,
  LayoutTemplate,
  Lightbulb,
  Trophy,
  ArrowRight,
  Coins,
  Sliders,
  Shield,
  Users,
  Heart,
  Star,
  Target,
  Zap,
  Menu,
  Quote,
  Check,
  Search,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import BrandLogo from '../components/BrandLogo';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODELS = [
  { id: 'claude-sonnet-4-6', provider: 'anthropic', label: 'Claude 4.5', desc: 'Best for coding', badge: 'A' },
  { id: 'gpt-5', provider: 'openai', label: 'GPT-5', desc: 'Best for creative', badge: 'O' },
  { id: 'gemini-2.5-pro', provider: 'google', label: 'Gemini 2.5', desc: 'Multimodal & fast', badge: 'G' },
];

const AGENTS = [
  { id: 'atw-1', label: 'ATW-1', desc: 'Default full-stack builder' },
  { id: 'atw-pro', label: 'ATW-Pro', desc: 'Higher context, deeper reasoning' },
  { id: 'atw-fast', label: 'ATW-Fast', desc: 'Quick drafts + prototypes' },
];

// Tabs mirror common builder taxonomies — Full Stack / Mobile / Landing / Brainstorm
const TABS = [
  {
    id: 'fullstack',
    label: 'Full Stack App',
    icon: Layers,
    placeholder: 'Build me a full-stack app that…',
    chips: [
      'A booking marketplace with Stripe',
      'A CRM for solo lawyers',
      'A fitness tracker with social feed',
      'An AI legal assistant',
    ],
  },
  {
    id: 'mobile',
    label: 'Mobile App',
    icon: Smartphone,
    placeholder: 'Design a mobile app that…',
    chips: [
      'A journaling app with mood AI',
      'A recipe scanner for pantry photos',
      'A meditation timer with streaks',
      'A local-events discovery app',
    ],
  },
  {
    id: 'landing',
    label: 'Landing Page',
    icon: LayoutTemplate,
    placeholder: 'Create a landing page for…',
    chips: [
      'A cinematic launch page for a coffee brand',
      'A dark-mode SaaS landing',
      'A portfolio site for a photographer',
      'A launch page for a crypto product',
    ],
  },
  {
    id: 'brainstorm',
    label: 'Brainstorm',
    icon: Lightbulb,
    placeholder: 'Help me brainstorm ideas for…',
    chips: [
      '10 startup ideas in edtech',
      'Names for my new agency',
      'A monetisation strategy for my newsletter',
      'A viral TikTok hook for my SaaS',
    ],
  },
];

function generateSessionId() {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export default function BuildPage() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('atw_session_id') || generateSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const [sessions, setSessions] = useState([]);
  const [modelId, setModelId] = useState(() => localStorage.getItem('atw_model') || 'claude-sonnet-4-6');
  const [agentId, setAgentId] = useState(() => localStorage.getItem('atw_agent') || 'atw-1');
  const [tabId, setTabId] = useState('fullstack');
  const [rightTab, setRightTab] = useState('preview'); // preview | code
  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const scrollRef = useRef(null);
  const homeTextareaRef = useRef(null);

  const selectedModel = MODELS.find((m) => m.id === modelId) || MODELS[0];
  const selectedAgent = AGENTS.find((a) => a.id === agentId) || AGENTS[0];
  const activeTab = TABS.find((t) => t.id === tabId) || TABS[0];

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('atw_user') || 'null'); } catch { return null; }
  }, []);

  useEffect(() => { localStorage.setItem('atw_session_id', sessionId); }, [sessionId]);
  useEffect(() => { localStorage.setItem('atw_model', modelId); }, [modelId]);
  useEffect(() => { localStorage.setItem('atw_agent', agentId); }, [agentId]);

  // Load sessions list
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/builder/sessions`, { credentials: 'include' });
        if (r.ok) setSessions(await r.json());
      } catch { /* noop */ }
    })();
  }, [streaming]);

  // Load current session messages
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/builder/sessions/${sessionId}/messages`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        } else setMessages([]);
      } catch { setMessages([]); }
    })();
  }, [sessionId]);

  // Pull initial prompt stashed by the landing page hero.
  // Prime the anon cookie via /builder/sessions GET first so chat/stream doesn't
  // race the cookie set (fixes iteration_4 P2 frontend race).
  useEffect(() => {
    (async () => {
      try {
        const initial = sessionStorage.getItem('atw:initialPrompt');
        if (!initial || messages.length !== 0) return;
        // Prime cookie first
        await fetch(`${API}/builder/sessions`, { credentials: 'include' }).catch(() => {});
        sessionStorage.removeItem('atw:initialPrompt');
        sendMessage(initial);
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamedText]);

  const sendMessage = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || streaming) return;
    setMessages((m) => [...m, { role: 'user', content: clean }]);
    setInput('');
    setStreaming(true);
    setStreamedText('');
    try {
      // Butler / Coach / Friend persona + user memory + custom SOUL — read from
      // localStorage (web) or the desktop bridge (Electron window.atw)
      const persona = localStorage.getItem('atw_persona') || null;
      let soul = localStorage.getItem('atw_soul') || null;
      let memory = localStorage.getItem('atw_memory') || null;
      if (window.atw?.isDesktop) {
        try {
          const [dSoul, dMemory] = await Promise.all([
            window.atw.soul.read(),
            window.atw.memory.read(),
          ]);
          if (dSoul) soul = dSoul;
          if (dMemory) memory = dMemory;
        } catch { /* noop */ }
      }
      const resp = await fetch(`${API}/builder/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: clean,
          model: selectedModel.id,
          provider: selectedModel.provider,
          persona,
          soul,
          memory,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error('Stream failed');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembled = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const chunk of parts) {
          const line = chunk.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evt = JSON.parse(payload);
            if (evt.type === 'delta') {
              assembled += evt.content;
              setStreamedText(assembled);
            } else if (evt.type === 'error') {
              toast({ title: 'AI error', description: evt.message });
            }
          } catch { /* ignore */ }
        }
      }
      setMessages((m) => [...m, { role: 'assistant', content: assembled }]);
      setStreamedText('');
    } catch (e) {
      toast({ title: 'Something went wrong', description: e.message || 'Please try again.' });
    } finally {
      setStreaming(false);
    }
  };

  const newSession = () => {
    const id = generateSessionId();
    setSessionId(id);
    setMessages([]);
    setStreamedText('');
  };

  const signOut = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* noop */ }
    localStorage.removeItem('atw_user');
    navigate('/');
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content || streamedText;

  const isHome = messages.length === 0 && !streaming && !streamedText;

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F33 0%, transparent 70%)' }}
      />
      {/* Star field */}
      <StarField />

      <BuilderHeader
        user={user}
        onSignOut={signOut}
        onDeploy={() => toast({ title: 'Deploy queued', description: 'Live deploys unlock on a paid plan.' })}
        title={sessions.find((s) => s.session_id === sessionId)?.title}
      />

      {isHome ? (
        <BuilderHome
          activeTab={activeTab}
          tabId={tabId}
          onTab={setTabId}
          input={input}
          setInput={setInput}
          onSubmit={() => sendMessage()}
          streaming={streaming}
          selectedAgent={selectedAgent}
          selectedModel={selectedModel}
          showAgentMenu={showAgentMenu}
          setShowAgentMenu={setShowAgentMenu}
          showModelMenu={showModelMenu}
          setShowModelMenu={setShowModelMenu}
          onAgent={setAgentId}
          onModel={setModelId}
          textareaRef={homeTextareaRef}
          sessions={sessions}
          onOpenSession={(id) => setSessionId(id)}
        />
      ) : (
        <BuilderChat
          scrollRef={scrollRef}
          messages={messages}
          streaming={streaming}
          streamedText={streamedText}
          input={input}
          setInput={setInput}
          onSend={() => sendMessage()}
          sessions={sessions}
          sessionId={sessionId}
          onOpenSession={setSessionId}
          onNewSession={newSession}
          rightTab={rightTab}
          setRightTab={setRightTab}
          lastAssistant={lastAssistant}
          selectedAgent={selectedAgent}
          selectedModel={selectedModel}
          onAgent={setAgentId}
          onModel={setModelId}
          showAgentMenu={showAgentMenu}
          setShowAgentMenu={setShowAgentMenu}
          showModelMenu={showModelMenu}
          setShowModelMenu={setShowModelMenu}
        />
      )}

      <Toaster />
    </div>
  );
}

/* ==========================================================
   HEADER
   ========================================================== */
function BuilderHeader({ user, onSignOut, onDeploy, title }) {
  const navigate = useNavigate();
  const credits = user?.credits ?? 100;
  const [soulMeta, setSoulMeta] = useState(() => {
    try { return JSON.parse(localStorage.getItem('atw_soul_meta') || 'null'); } catch { return null; }
  });
  const [soulPickerOpen, setSoulPickerOpen] = useState(false);
  // Refresh when localStorage changes (e.g., user came back from /souls)
  useEffect(() => {
    const onFocus = () => {
      try { setSoulMeta(JSON.parse(localStorage.getItem('atw_soul_meta') || 'null')); } catch { /* noop */ }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const clearSoul = () => {
    localStorage.removeItem('atw_soul');
    localStorage.removeItem('atw_soul_meta');
    setSoulMeta(null);
    toast({ title: 'SOUL cleared', description: 'Back to the default builder personality.' });
  };
  const applySoul = (soul) => {
    localStorage.setItem('atw_soul', soul.system_prompt);
    const meta = { name: soul.name, emoji: soul.emoji, slug: soul.slug };
    localStorage.setItem('atw_soul_meta', JSON.stringify(meta));
    setSoulMeta(meta);
    setSoulPickerOpen(false);
    fetch(`${API}/souls/${soul.slug}/use`, { method: 'POST' }).catch(() => {});
    toast({
      title: `${soul.emoji || '✨'} ${soul.name} activated`,
      description: 'Your next reply will speak in this personality.',
    });
  };
  return (
    <header className="relative z-30 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
      <div className="mx-auto max-w-[1600px] px-5 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={() => navigate('/')} data-testid="builder-home-link" className="shrink-0">
            <BrandLogo size={28} />
          </button>
          <span className="hidden sm:inline text-xs font-mono uppercase tracking-[0.3em] text-white/40 border-l border-white/10 pl-4">
            Builder
          </span>
          {title && (
            <>
              <ChevronRight size={14} className="text-white/30 hidden md:inline" />
              <span className="hidden md:inline text-sm text-white/60 truncate max-w-[280px]">{title}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* SOUL chip — click to open picker; clear × icon on the right */}
          <div className="hidden md:block relative">
            {soulMeta ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] pl-2 pr-1 py-1 text-[12px] text-white/80" data-testid="builder-soul-chip">
                <button
                  onClick={() => setSoulPickerOpen((v) => !v)}
                  data-testid="builder-soul-chip-btn"
                  className="inline-flex items-center gap-1.5 hover:text-white transition"
                  title="Swap SOUL"
                >
                  <span className="text-[14px]">{soulMeta.emoji || '✨'}</span>
                  <span className="max-w-[120px] truncate">{soulMeta.name}</span>
                  <ChevronDown size={11} className="text-white/50" />
                </button>
                <button
                  onClick={clearSoul}
                  data-testid="builder-soul-clear"
                  className="h-5 w-5 rounded-full hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition ml-1"
                  title="Clear SOUL"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSoulPickerOpen((v) => !v)}
                data-testid="builder-soul-add-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-white/20 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/30 px-3 py-1 text-[12px] text-white/60 hover:text-white/85 transition"
              >
                <Sparkles size={12} className="text-[#00F0FF]" /> Add SOUL
              </button>
            )}
            {soulPickerOpen && (
              <SoulPickerPopover
                onClose={() => setSoulPickerOpen(false)}
                onPick={applySoul}
                currentSlug={soulMeta?.slug}
              />
            )}
          </div>

          {/* Credits chip */}
          <button
            data-testid="builder-credits-chip"
            onClick={() => navigate('/#pricing')}
            className="group relative inline-flex items-center gap-2 rounded-full pl-3 pr-4 py-1.5 text-[12px] font-semibold text-black transition"
            style={{ background: 'linear-gradient(90deg, #FFE066 0%, #FFA500 100%)' }}
            title="Buy more credits"
          >
            <Coins size={14} />
            <span>{credits} credits</span>
            <span className="ml-1 rounded-full bg-black/85 text-[10px] px-1.5 py-0.5 text-white">+20% bonus</span>
          </button>

          {/* Advisor chip */}
          <button
            data-testid="builder-advisor-chip"
            onClick={() =>
              toast({ title: 'Meet Your AI Guide', description: 'Coming soon — you\'ll be able to book a real human strategist.' })
            }
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px] text-white/80 hover:bg-white/[0.08] transition"
          >
            <span className="relative flex h-6 w-6 rounded-full overflow-hidden">
              <span className="absolute inset-0 bg-gradient-to-br from-[#00F0FF] to-[#FF007F]" />
              <span className="absolute inset-[2px] rounded-full bg-black/70 flex items-center justify-center text-[10px] font-bold">
                🎯
              </span>
            </span>
            Meet Your AI Guide
          </button>

          {/* Deploy button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeploy}
            data-testid="builder-deploy-btn"
            className="h-9 text-white/80 hover:text-white hover:bg-white/[0.06]"
          >
            <Rocket size={14} className="mr-1.5" /> Deploy
          </Button>

          {/* User avatar */}
          <div className="h-9 w-px bg-white/10 mx-1 hidden sm:block" />
          <div className="hidden sm:flex items-center gap-2 pr-1">
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-black text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              {(user?.name || user?.email || 'G').slice(0, 1).toUpperCase()}
            </div>
            <span className="text-xs text-white/70 max-w-[140px] truncate">{user?.name || user?.email || 'Guest'}</span>
          </div>

          <Button variant="ghost" size="sm" onClick={onSignOut} data-testid="builder-signout-btn" className="text-white/50 hover:text-white">
            <LogOut size={14} />
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ==========================================================
   HOME MODE (Emergent-style landing hero inside the builder)
   ========================================================== */
function BuilderHome(props) {
  const {
    activeTab,
    tabId,
    onTab,
    input,
    setInput,
    onSubmit,
    streaming,
    selectedAgent,
    selectedModel,
    showAgentMenu,
    setShowAgentMenu,
    showModelMenu,
    setShowModelMenu,
    onAgent,
    onModel,
    textareaRef,
    sessions,
    onOpenSession,
  } = props;

  return (
    <main className="relative z-10 mx-auto max-w-6xl px-5 pt-10 pb-24">
      {/* Top waitlist banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="builder-waitlist-banner"
        className="mx-auto mb-14 max-w-3xl flex items-center gap-3 rounded-full border border-white/10 bg-gradient-to-r from-white/[0.03] via-white/[0.05] to-white/[0.03] backdrop-blur-xl pl-3 pr-1 py-1"
      >
        <span className="relative flex h-8 w-8 shrink-0 rounded-full overflow-hidden">
          <span className="absolute inset-0 bg-gradient-to-br from-[#00F0FF] to-[#FF007F]" />
          <span className="absolute inset-[2px] rounded-full bg-black/80 flex items-center justify-center text-[13px]">
            🤖
          </span>
        </span>
        <span className="flex-1 text-[13px] text-white/85 truncate">
          <span className="font-semibold text-white">Priority Builder Support</span> — reserve your spot for early access.
        </span>
        <button className="shrink-0 rounded-full bg-white text-black text-[12px] font-semibold pl-4 pr-3 py-1.5 flex items-center gap-1.5 hover:bg-white/90 transition">
          Join waitlist <ArrowRight size={12} />
        </button>
      </motion.div>

      {/* Project chip */}
      <div className="flex justify-center mb-5">
        <button className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-1.5 text-[12.5px] text-white/80 hover:bg-white/[0.06] transition">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          Untitled Project
          <ChevronDown size={12} className="text-white/40" />
        </button>
      </div>

      {/* Hero heading */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="text-center font-display font-black tracking-[-0.03em] text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #94a3b8 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        What will you build today?
      </motion.h1>

      {/* Tabs */}
      <div className="mt-10 flex justify-center">
        <div
          data-testid="builder-tabs"
          className="inline-flex flex-wrap gap-1 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-1"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.id === tabId;
            return (
              <button
                key={t.id}
                data-testid={`builder-tab-${t.id}`}
                onClick={() => onTab(t.id)}
                className={`relative inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-medium transition ${
                  active ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="tab-active"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(0,240,255,0.15) 0%, rgba(255,0,127,0.15) 100%)',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <Icon size={14} className="relative z-10" />
                <span className="relative z-10">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Prompt box */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 mx-auto max-w-3xl"
      >
        <div className="relative rounded-3xl border border-white/10 bg-black/50 backdrop-blur-xl overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
          {/* Corner glows */}
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
          />

          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder={activeTab.placeholder}
            rows={3}
            data-testid="builder-prompt-input"
            className="relative bg-transparent border-0 focus-visible:ring-0 text-white placeholder:text-white/35 text-[16px] md:text-[17px] leading-relaxed px-6 py-5 min-h-[130px] resize-none"
          />

          {/* Tool row */}
          <div className="relative flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              <ToolIcon icon={Paperclip} title="Attach file" />
              <ToolIcon icon={GitBranch} title="Import from GitHub" />
              <ChipButton
                testid="builder-agent-chip"
                open={showAgentMenu}
                onOpen={() => setShowAgentMenu((v) => !v)}
                icon={Cpu}
                label={selectedAgent.label}
              >
                {showAgentMenu && (
                  <Popover onClose={() => setShowAgentMenu(false)}>
                    <PopoverTitle>Select agent</PopoverTitle>
                    {AGENTS.map((a) => (
                      <PopoverItem
                        key={a.id}
                        selected={a.id === selectedAgent.id}
                        onClick={() => { onAgent(a.id); setShowAgentMenu(false); }}
                        title={a.label}
                        desc={a.desc}
                      />
                    ))}
                  </Popover>
                )}
              </ChipButton>
              <ChipButton
                testid="builder-model-chip"
                open={showModelMenu}
                onOpen={() => setShowModelMenu((v) => !v)}
                icon={Wand2}
                label={selectedModel.label}
              >
                {showModelMenu && (
                  <Popover onClose={() => setShowModelMenu(false)}>
                    <PopoverTitle>Select model</PopoverTitle>
                    {MODELS.map((m) => (
                      <PopoverItem
                        key={m.id}
                        selected={m.id === selectedModel.id}
                        onClick={() => { onModel(m.id); setShowModelMenu(false); }}
                        title={m.label}
                        desc={m.desc}
                      />
                    ))}
                  </Popover>
                )}
              </ChipButton>
              <ChipButton
                testid="builder-maxx-chip"
                icon={UserRound}
                label="Maxx"
                trailingDot
              />
            </div>
            <div className="flex items-center gap-1.5">
              <ToolIcon icon={Sliders} title="Advanced settings" />
              <ToolIcon icon={Mic} title="Voice input" />
              <button
                onClick={onSubmit}
                disabled={streaming || !input.trim()}
                data-testid="builder-prompt-submit"
                className="ml-1 h-10 w-10 rounded-full flex items-center justify-center disabled:opacity-40 transition hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }}
                title="Ship it"
              >
                {streaming ? (
                  <Loader2 size={16} className="animate-spin text-black" />
                ) : (
                  <ArrowRight size={16} className="text-black" strokeWidth={2.5} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Starter chips */}
        <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
          <span className="text-[11px] font-mono uppercase tracking-widest text-white/40 mr-1">Try:</span>
          {activeTab.chips.map((c) => (
            <button
              key={c}
              onClick={() => { setInput(c); textareaRef.current?.focus(); }}
              data-testid={`builder-starter-${c.slice(0, 20)}`}
              className="rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 text-white/80 text-[12px] px-3 py-1.5 transition"
            >
              {c}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Contest ribbon */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.35 }}
        className="mx-auto mt-10 max-w-3xl flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-2.5"
      >
        <Trophy size={16} className="text-amber-400 shrink-0" />
        <span className="flex-1 text-[13px] text-white/85">
          <span className="font-semibold text-white">Around the World Builders Contest</span> — win up to
          <span className="mx-1 font-semibold text-amber-300">$10,000</span>
          in credits &amp; hosting. Coming soon.
        </span>
        <button
          onClick={() => toast({ title: 'Contest launches soon', description: 'Sign up on the landing page to get notified.' })}
          className="shrink-0 rounded-full bg-white text-black text-[12px] font-semibold px-3 py-1 hover:bg-white/90 transition"
        >
          Join
        </button>
      </motion.div>

      {/* Web ⇄ Mobile floating card */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="fixed left-6 bottom-24 hidden xl:flex flex-col items-center pointer-events-none"
      >
        <div className="relative w-32 h-32 rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #F7B801 100%)' }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-white text-center px-2">
            <div className="text-[13px] font-bold flex items-center gap-1">
              Web <span className="text-lg">⇄</span> Mobile
            </div>
            <div className="text-[10px] mt-1 font-medium opacity-90">In one click</div>
          </div>
        </div>
      </motion.div>

      {/* Recent tasks / Deployed apps */}
      <RecentTasks sessions={sessions} onOpen={onOpenSession} />
    </main>
  );
}

/* ==========================================================
   Small UI helpers used by BuilderHome
   ========================================================== */
function ToolIcon({ icon: Icon, title, onClick }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className="h-9 w-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition"
    >
      <Icon size={15} />
    </button>
  );
}

function ChipButton({ icon: Icon, label, open, onOpen, children, trailingDot, testid }) {
  return (
    <div className="relative">
      <button
        onClick={onOpen}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[12px] font-medium transition ${
          open
            ? 'bg-white/[0.1] text-white border border-white/20'
            : 'text-white/75 hover:text-white hover:bg-white/[0.06] border border-white/10'
        }`}
      >
        <Icon size={13} />
        <span>{label}</span>
        {trailingDot ? (
          <span className="ml-1 h-1.5 w-1.5 rounded-full bg-white" />
        ) : (
          onOpen ? <ChevronDown size={12} className="text-white/50" /> : null
        )}
      </button>
      {children}
    </div>
  );
}

function Popover({ children, onClose }) {
  useEffect(() => {
    const close = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return (
    <div className="absolute left-0 bottom-full mb-2 min-w-[240px] rounded-xl border border-white/10 bg-[#0a0a0f]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50">
      {children}
    </div>
  );
}
function PopoverTitle({ children }) {
  return <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-white/40 font-semibold">{children}</div>;
}
function PopoverItem({ selected, onClick, title, desc }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 hover:bg-white/[0.06] transition ${
        selected ? 'bg-white/[0.04]' : ''
      }`}
    >
      <div className={`text-[13px] font-semibold ${selected ? 'text-white' : 'text-white/85'}`}>{title}</div>
      <div className="text-[11px] text-white/50 mt-0.5">{desc}</div>
    </button>
  );
}

function RecentTasks({ sessions, onOpen }) {
  const [tab, setTab] = useState('recent'); // recent | deployed
  return (
    <div className="mt-16">
      <div className="flex items-center gap-1 border-b border-white/10 pb-2">
        <button
          onClick={() => setTab('recent')}
          data-testid="recent-tasks-tab"
          className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg flex items-center gap-1.5 transition ${
            tab === 'recent' ? 'text-white' : 'text-white/50 hover:text-white/80'
          }`}
        >
          <FileCode2 size={13} /> Recent Tasks
        </button>
        <span className="text-white/20 mx-1">|</span>
        <button
          onClick={() => setTab('deployed')}
          data-testid="deployed-apps-tab"
          className={`px-3 py-1.5 text-[13px] font-semibold rounded-lg flex items-center gap-1.5 transition ${
            tab === 'deployed' ? 'text-white' : 'text-white/50 hover:text-white/80'
          }`}
        >
          <Globe2 size={13} /> Deployed Apps
        </button>
      </div>

      {tab === 'recent' ? (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">
                <th className="px-5 py-3 font-normal">ID</th>
                <th className="px-5 py-3 font-normal">Task</th>
                <th className="px-5 py-3 font-normal text-right">Last modified</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-10 text-center text-white/40 text-[13px]">
                  No builds yet. Type a prompt above to get started.
                </td></tr>
              )}
              {sessions.slice(0, 8).map((s) => (
                <tr
                  key={s.session_id}
                  onClick={() => onOpen(s.session_id)}
                  className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer transition"
                >
                  <td className="px-5 py-3 font-mono text-[11px] text-white/50">{s.session_id.slice(0, 10)}</td>
                  <td className="px-5 py-3 text-white/85 truncate max-w-[520px]">{s.title}</td>
                  <td className="px-5 py-3 text-right text-white/50 text-[12px]">{formatRelative(s.last_updated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-10 text-center text-white/40 text-[13px]">
          No deployed apps yet. Ship a build to see it here.
        </div>
      )}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

/* ==========================================================
   STAR FIELD (subtle animated sparkles background)
   ========================================================== */
function StarField() {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 40; i++) {
      arr.push({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 1.4 + 0.3,
        delay: Math.random() * 4,
        duration: 2 + Math.random() * 3,
      });
    }
    return arr;
  }, []);
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            opacity: 0.6,
            animation: `starTwinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}

/* ==========================================================
   CHAT MODE — sidebar + chat + preview/code
   ========================================================== */
function BuilderChat(props) {
  const {
    scrollRef,
    messages,
    streaming,
    streamedText,
    input,
    setInput,
    onSend,
    sessions,
    sessionId,
    onOpenSession,
    onNewSession,
    rightTab,
    setRightTab,
    lastAssistant,
    selectedAgent,
    selectedModel,
    onAgent,
    onModel,
    showAgentMenu,
    setShowAgentMenu,
    showModelMenu,
    setShowModelMenu,
  } = props;

  return (
    <div className="relative z-10 flex h-[calc(100vh-58px)]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 hidden xl:flex flex-col border-r border-white/10 bg-black/30 backdrop-blur-xl">
        <div className="p-3">
          <Button
            onClick={onNewSession}
            data-testid="builder-new-build-btn"
            className="w-full h-10 rounded-xl text-black font-semibold justify-start"
            style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
          >
            <Plus size={15} className="mr-1.5" /> New build
          </Button>
        </div>
        <div className="px-3 pt-1 pb-2 text-[10px] font-mono tracking-[0.2em] text-white/40 uppercase">
          Recent builds
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
          {sessions.length === 0 && (
            <div className="text-xs text-white/40 px-2 py-3">No builds yet.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => onOpenSession(s.session_id)}
              data-testid={`builder-session-${s.session_id.slice(0, 8)}`}
              className={`w-full text-left px-2.5 py-2 rounded-lg text-[13px] truncate transition ${
                s.session_id === sessionId
                  ? 'bg-white/[0.08] text-white font-medium'
                  : 'text-white/60 hover:bg-white/[0.05] hover:text-white'
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
      </aside>

      {/* Chat pane */}
      <main className="flex-1 flex flex-col min-w-0 border-r border-white/10">
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
            {messages.map((m, i) => (
              <ChatMessage key={i} role={m.role} content={m.content} />
            ))}
            {streaming && <ChatMessage role="assistant" content={streamedText} streaming />}
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-white/10 bg-black/40 backdrop-blur-xl px-4 py-3">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-black/50 overflow-hidden focus-within:border-white/25 transition">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                placeholder="Reply to the AI, refine the design, add a feature…"
                rows={2}
                data-testid="builder-chat-input"
                className="min-h-[64px] resize-none border-0 focus-visible:ring-0 text-[14.5px] px-4 py-3 bg-transparent text-white placeholder:text-white/35"
              />
              <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.06]">
                <div className="flex items-center gap-1.5">
                  <ToolIcon icon={Paperclip} title="Attach" />
                  <ChipButton
                    testid="chat-agent-chip"
                    open={showAgentMenu}
                    onOpen={() => setShowAgentMenu((v) => !v)}
                    icon={Cpu}
                    label={selectedAgent.label}
                  >
                    {showAgentMenu && (
                      <Popover onClose={() => setShowAgentMenu(false)}>
                        <PopoverTitle>Select agent</PopoverTitle>
                        {AGENTS.map((a) => (
                          <PopoverItem
                            key={a.id}
                            selected={a.id === selectedAgent.id}
                            onClick={() => { onAgent(a.id); setShowAgentMenu(false); }}
                            title={a.label}
                            desc={a.desc}
                          />
                        ))}
                      </Popover>
                    )}
                  </ChipButton>
                  <ChipButton
                    testid="chat-model-chip"
                    open={showModelMenu}
                    onOpen={() => setShowModelMenu((v) => !v)}
                    icon={Wand2}
                    label={selectedModel.label}
                  >
                    {showModelMenu && (
                      <Popover onClose={() => setShowModelMenu(false)}>
                        <PopoverTitle>Select model</PopoverTitle>
                        {MODELS.map((m) => (
                          <PopoverItem
                            key={m.id}
                            selected={m.id === selectedModel.id}
                            onClick={() => { onModel(m.id); setShowModelMenu(false); }}
                            title={m.label}
                            desc={m.desc}
                          />
                        ))}
                      </Popover>
                    )}
                  </ChipButton>
                </div>
                <div className="flex items-center gap-1.5">
                  <ToolIcon icon={Mic} title="Voice" />
                  <button
                    onClick={onSend}
                    disabled={streaming || !input.trim()}
                    data-testid="builder-chat-send"
                    className="h-9 w-9 rounded-full flex items-center justify-center disabled:opacity-40 transition hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }}
                  >
                    {streaming ? <Loader2 size={14} className="animate-spin text-black" /> : <Send size={14} className="text-black" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="text-[10.5px] text-white/40 mt-2 text-center">
              Around the World AI can make mistakes. Review generated code before shipping.
            </div>
          </div>
        </div>
      </main>

      {/* Right preview / code */}
      <aside className="w-full md:w-[380px] xl:w-[440px] shrink-0 hidden md:flex flex-col bg-black/30 backdrop-blur-xl">
        <div className="border-b border-white/10 px-3 py-2 flex items-center gap-1">
          <button
            onClick={() => setRightTab('preview')}
            data-testid="preview-tab-btn"
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition ${
              rightTab === 'preview'
                ? 'bg-white/[0.08] text-white'
                : 'text-white/50 hover:text-white'
            }`}
          >
            <Eye size={12} /> Preview
          </button>
          <button
            onClick={() => setRightTab('code')}
            data-testid="code-tab-btn"
            className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition ${
              rightTab === 'code'
                ? 'bg-white/[0.08] text-white'
                : 'text-white/50 hover:text-white'
            }`}
          >
            <FileCode2 size={12} /> Code
          </button>
          <div className="flex-1" />
          <span className="text-[10px] font-mono text-white/40 uppercase tracking-[0.2em] flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {rightTab === 'preview' ? (
            <PreviewPanel content={lastAssistant} />
          ) : (
            <CodePanel content={lastAssistant} />
          )}
        </div>
      </aside>
    </div>
  );
}

/* ==========================================================
   Chat pieces
   ========================================================== */
function stripPreviewBlock(content) {
  if (!content) return content;
  // Remove the atw-preview fenced block (visible or partially streaming) from chat display
  return content.replace(/```atw-preview[\s\S]*?```/gi, '').replace(/```atw-preview[\s\S]*$/i, '').trim();
}

function CodeBlock({ children }) {
  // children is a <code> element wrapping the raw string
  let rawText = '';
  let lang = 'code';
  try {
    const codeEl = Array.isArray(children) ? children[0] : children;
    const className = codeEl?.props?.className || '';
    const langMatch = className.match(/language-(\w+)/);
    if (langMatch) lang = langMatch[1];
    rawText = codeEl?.props?.children;
    if (Array.isArray(rawText)) rawText = rawText.join('');
    if (typeof rawText !== 'string') rawText = String(rawText || '');
  } catch { /* noop */ }
  return (
    <div className="my-3 rounded-xl overflow-hidden border border-white/10 bg-[#0a0a12]">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06] bg-white/[0.03]">
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">{lang}</span>
        <button
          onClick={() => {
            navigator.clipboard.writeText(rawText);
            toast({ title: 'Copied', description: 'Code copied to clipboard.' });
          }}
          className="text-[10px] text-white/50 hover:text-white flex items-center gap-1 transition"
        >
          <Copy size={10} /> Copy
        </button>
      </div>
      <pre className="p-3 overflow-x-auto text-[12.5px] leading-[1.6] text-cyan-100 font-mono">
        {rawText}
      </pre>
    </div>
  );
}

// Static markdown component overrides — module-level so React doesn't re-mount them
const MdH1 = (p) => <h2 className="text-lg font-black text-white mt-3 mb-2 tracking-tight" {...p} />;
const MdH2 = (p) => <h3 className="text-[15px] font-bold text-white mt-3 mb-1.5 tracking-tight" {...p} />;
const MdH3 = (p) => <h4 className="text-[13.5px] font-bold text-white/90 mt-2.5 mb-1 tracking-tight uppercase" {...p} />;
const MdP  = (p) => <p className="my-2 text-white/85" {...p} />;
const MdUl = (p) => <ul className="my-2 space-y-1 list-none pl-0" {...p} />;
const MdLi = (p) => (
  <li className="flex items-start gap-2 text-white/85">
    <span className="mt-1.5 h-1 w-1 rounded-full bg-[#00F0FF] shrink-0" />
    <span {...p} />
  </li>
);
const MdStrong = (p) => <strong className="font-bold text-white" {...p} />;
const MdEm = (p) => <em className="italic text-white/90" {...p} />;
const MdCode = ({ inline, ...p }) =>
  inline ? (
    <code className="rounded bg-white/10 text-cyan-200 px-1.5 py-0.5 text-[13px] font-mono" {...p} />
  ) : (
    <code {...p} />
  );
const MdPre = ({ children }) => <CodeBlock>{children}</CodeBlock>;
const MdA = (p) => (
  <a className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />
);
const MdBlockquote = (p) => (
  <blockquote className="border-l-2 border-cyan-400/40 pl-3 my-2 text-white/75 italic" {...p} />
);

const MD_COMPONENTS = {
  h1: MdH1,
  h2: MdH2,
  h3: MdH3,
  p: MdP,
  ul: MdUl,
  li: MdLi,
  strong: MdStrong,
  em: MdEm,
  code: MdCode,
  pre: MdPre,
  a: MdA,
  blockquote: MdBlockquote,
};

function ChatMessage({ role, content, streaming }) {
  const isUser = role === 'user';
  const displayContent = isUser ? content : stripPreviewBlock(content);
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`h-9 w-9 rounded-xl inline-flex items-center justify-center shrink-0 shadow-lg ${
          isUser ? 'text-black text-xs font-bold' : 'text-white'
        }`}
        style={
          isUser
            ? { background: 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)' }
            : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }
        }
      >
        {isUser ? 'You' : <Sparkles size={14} className="text-[#00F0FF]" />}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-5 py-3.5 ${
          isUser
            ? 'text-white rounded-tr-sm shadow-lg'
            : 'text-white/90 rounded-tl-sm border border-white/10'
        }`}
        style={
          isUser
            ? { background: 'linear-gradient(135deg, rgba(0,240,255,0.14) 0%, rgba(255,0,127,0.14) 100%)', backdropFilter: 'blur(6px)' }
            : { background: 'rgba(255,255,255,0.03)' }
        }
      >
        {isUser ? (
          <div className="whitespace-pre-wrap text-[15px] leading-[1.65] font-sans">{content}</div>
        ) : displayContent ? (
          <div className="assistant-md text-[15px] leading-[1.7] font-sans">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={MD_COMPONENTS}
            >
              {displayContent}
            </ReactMarkdown>
            {streaming && (
              <span className="inline-block w-1.5 h-4 align-middle ml-1 bg-[#00F0FF] animate-pulse rounded-sm" />
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 py-1">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="ml-2 text-xs text-white/50">Designing your preview…</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================
   PREVIEW PANEL — rich multi-section mock website
   ========================================================== */
const FEATURE_ICONS = {
  sparkles: Sparkles,
  zap: Zap,
  shield: Shield,
  globe: Globe2,
  users: Users,
  heart: Heart,
  rocket: Rocket,
  star: Star,
  target: Target,
  cpu: Cpu,
};

// Fallback vibes when the AI hasn't returned atw-preview JSON yet
const INDUSTRY_DEFAULTS = {
  coffee:      { primary: '#8B4513', accent: '#D97706', theme: 'warm' },
  fitness:     { primary: '#DC2626', accent: '#F59E0B', theme: 'electric' },
  portfolio:   { primary: '#0F172A', accent: '#94A3B8', theme: 'mono' },
  ecommerce:   { primary: '#7C3AED', accent: '#EC4899', theme: 'electric' },
  saas:        { primary: '#2563EB', accent: '#22D3EE', theme: 'cool' },
  agency:      { primary: '#0EA5E9', accent: '#F472B6', theme: 'cool' },
  ai:          { primary: '#7C3AED', accent: '#00F0FF', theme: 'electric' },
  travel:      { primary: '#0891B2', accent: '#F59E0B', theme: 'warm' },
  crypto:      { primary: '#F59E0B', accent: '#22D3EE', theme: 'dark' },
  health:      { primary: '#10B981', accent: '#60A5FA', theme: 'cool' },
  food:        { primary: '#EA580C', accent: '#FACC15', theme: 'warm' },
  'real-estate': { primary: '#1E3A8A', accent: '#F59E0B', theme: 'cool' },
  education:   { primary: '#4338CA', accent: '#F97316', theme: 'cool' },
  other:       { primary: '#00F0FF', accent: '#FF007F', theme: 'electric' },
};

function extractPreviewJson(content) {
  if (!content) return null;
  const m = content.match(/```atw-preview\s*\n([\s\S]*?)(?:```|$)/i);
  if (!m) return null;
  try {
    return JSON.parse(m[1].trim());
  } catch {
    // Streaming: try to salvage incomplete JSON by trimming to last valid brace
    try {
      const raw = m[1].trim();
      const lastBrace = raw.lastIndexOf('}');
      if (lastBrace > 0) return JSON.parse(raw.slice(0, lastBrace + 1));
    } catch { /* still streaming */ }
    return null;
  }
}

function hexAlpha(hex, alpha) {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function PreviewPanel({ content }) {
  if (!content) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center">
        <div>
          <MonitorSmartphone size={40} className="text-white/20 mx-auto mb-4" />
          <div className="text-sm font-medium text-white/60">Live preview will appear here</div>
          <div className="text-xs text-white/40 mt-1">Send a message to start building.</div>
        </div>
      </div>
    );
  }

  const meta = extractPreviewJson(content);
  const streaming = !meta;
  const industryDefaults = INDUSTRY_DEFAULTS[meta?.industry] || INDUSTRY_DEFAULTS.other;

  const primary = meta?.primary || industryDefaults.primary;
  const accent  = meta?.accent  || industryDefaults.accent;
  const title   = meta?.title   || 'Your app is taking shape';
  const tagline = meta?.tagline || 'Generated by Around the World AI';
  const heroHead = meta?.hero?.headline || title;
  const heroSub  = meta?.hero?.sub || tagline;
  const heroCta  = meta?.hero?.cta || 'Get started';
  const nav = meta?.nav?.slice(0, 5) || ['Home', 'Features', 'Pricing', 'Contact'];
  const features = meta?.features?.slice(0, 4) || [];
  const ctaTitle = meta?.cta?.title || 'Ready to launch?';
  const ctaBtn   = meta?.cta?.button || 'Start now';
  const quote    = meta?.testimonial?.quote || null;
  const author   = meta?.testimonial?.author || null;
  const domainSlug = (title || 'your-app').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24) || 'your-app';

  return (
    <div className="p-3">
      {/* Browser chrome */}
      <div className="rounded-t-xl bg-white/[0.05] border border-b-0 border-white/10 px-3 py-2 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <span className="mx-2 flex-1 rounded-md bg-black/40 border border-white/10 px-2 py-0.5 text-[10.5px] text-white/60 truncate font-mono">
          preview.aroundtheworldai.com/{domainSlug}
        </span>
        {streaming && (
          <span className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 flex items-center gap-1">
            <Loader2 size={9} className="animate-spin" /> building
          </span>
        )}
      </div>

      {/* Mock site body */}
      <div
        className="border border-white/10 rounded-b-xl overflow-hidden shadow-xl"
        style={{ background: '#ffffff', color: '#0f172a' }}
      >
        {/* Nav */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b border-slate-100"
          style={{ background: hexAlpha(primary, 0.03) }}
        >
          <div className="flex items-center gap-2">
            <div
              className="h-6 w-6 rounded-md flex items-center justify-center text-white text-[10px] font-black"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}
            >
              {(title[0] || 'A').toUpperCase()}
            </div>
            <span className="text-[11px] font-bold tracking-tight" style={{ color: primary }}>
              {title.slice(0, 24)}
            </span>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {nav.map((item, i) => (
              <span key={i} className="text-[10px] text-slate-500 font-medium">
                {item}
              </span>
            ))}
          </div>
          <button
            className="text-[10px] font-semibold rounded-md text-white px-2.5 py-1"
            style={{ background: primary }}
          >
            {heroCta}
          </button>
          <Menu size={12} className="text-slate-400 md:hidden" />
        </div>

        {/* Hero */}
        <div
          className="px-5 py-8 relative overflow-hidden"
          style={{
            background: `radial-gradient(600px 260px at 100% 0%, ${hexAlpha(accent, 0.18)} 0%, transparent 60%),
                        radial-gradient(500px 220px at 0% 100%, ${hexAlpha(primary, 0.14)} 0%, transparent 60%),
                        linear-gradient(180deg, #ffffff, ${hexAlpha(primary, 0.03)})`,
          }}
        >
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest mb-3"
            style={{ background: hexAlpha(primary, 0.1), color: primary }}
          >
            <Sparkles size={9} /> {meta?.industry || 'launch'}
          </div>
          <h1
            className="font-black leading-tight tracking-tight text-[20px] md:text-[22px]"
            style={{ color: '#0f172a' }}
          >
            {heroHead}
          </h1>
          <p className="mt-2 text-[11.5px] text-slate-600 leading-relaxed max-w-[95%]">
            {heroSub}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <button
              className="rounded-md text-white text-[10.5px] font-bold px-3 py-1.5 shadow"
              style={{ background: `linear-gradient(90deg, ${primary} 0%, ${accent} 100%)` }}
            >
              {heroCta}
            </button>
            <button className="rounded-md border border-slate-200 text-slate-700 text-[10.5px] font-medium px-3 py-1.5">
              Learn more
            </button>
          </div>
          <div className="mt-4 flex items-center gap-3 text-[9px] text-slate-500">
            <div className="flex items-center gap-1">
              <Check size={9} style={{ color: primary }} />
              <span>No credit card</span>
            </div>
            <div className="flex items-center gap-1">
              <Check size={9} style={{ color: primary }} />
              <span>Free trial</span>
            </div>
          </div>
        </div>

        {/* Features grid */}
        {features.length > 0 && (
          <div className="px-5 py-6 bg-white">
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>
              What you get
            </div>
            <div className="grid grid-cols-2 gap-3">
              {features.map((f, i) => {
                const Icon = FEATURE_ICONS[f.icon] || Sparkles;
                return (
                  <div
                    key={i}
                    className="rounded-lg p-2.5 border border-slate-100"
                    style={{ background: hexAlpha(primary, 0.02) }}
                  >
                    <div
                      className="h-6 w-6 rounded-md inline-flex items-center justify-center text-white mb-1.5"
                      style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}
                    >
                      <Icon size={11} />
                    </div>
                    <div className="text-[11px] font-bold text-slate-900 leading-tight">
                      {f.title}
                    </div>
                    <div className="text-[9.5px] text-slate-500 mt-1 leading-snug line-clamp-2">
                      {f.desc}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Testimonial */}
        {quote && (
          <div className="px-5 py-5 border-t border-slate-100" style={{ background: hexAlpha(accent, 0.04) }}>
            <Quote size={16} style={{ color: primary }} />
            <p className="mt-1.5 text-[11.5px] italic text-slate-700 leading-relaxed">
              &ldquo;{quote}&rdquo;
            </p>
            {author && (
              <div className="mt-2 text-[9.5px] font-semibold text-slate-500 tracking-wide">
                — {author}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div
          className="px-5 py-6 text-center"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}
        >
          <div className="text-[13px] font-black text-white leading-tight">{ctaTitle}</div>
          <button className="mt-3 rounded-md bg-white text-[10.5px] font-bold px-4 py-1.5" style={{ color: primary }}>
            {ctaBtn}
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 flex items-center justify-between text-[9px] text-slate-500 bg-slate-50 border-t border-slate-100">
          <span>© {new Date().getFullYear()} {title.slice(0, 20)}</span>
          <span className="font-mono">Built with Around the World AI</span>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[10.5px] text-white/60 leading-relaxed">
        {streaming
          ? 'AI is drafting your app… the preview updates in real-time.'
          : 'Visual mock preview based on the AI blueprint. Live deploys unlock on a paid plan.'}
      </div>
    </div>
  );
}

function CodePanel({ content }) {
  const code = useMemo(() => {
    if (!content) return null;
    // Skip the atw-preview block and find the first real code snippet
    const cleaned = content.replace(/```atw-preview[\s\S]*?```/gi, '');
    const match = cleaned.match(/```(\w+)?\n([\s\S]*?)```/);
    if (match) {
      const lang = match[1] && match[1] !== 'atw-preview' ? match[1] : 'jsx';
      return { lang, body: match[2].trim() };
    }
    return null;
  }, [content]);

  if (!code) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center">
        <div>
          <Code2 size={40} className="text-white/20 mx-auto mb-4" />
          <div className="text-sm font-medium text-white/60">Generated code will appear here</div>
          <div className="text-xs text-white/40 mt-1">Ask the AI to build something.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <FileCode2 size={13} className="text-white/60" />
          <span className="text-[12px] font-mono text-white/80">snippet.{code.lang}</span>
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(code.body);
            toast({ title: 'Copied', description: 'Code copied to clipboard.' });
          }}
          className="text-[11px] text-white/60 hover:text-white flex items-center gap-1"
        >
          <Copy size={11} /> Copy
        </button>
      </div>
      <pre className="flex-1 overflow-auto text-[11.5px] leading-[1.6] p-4 bg-[#0a0a12] text-cyan-100 font-mono">
        <code>{code.body}</code>
      </pre>
    </div>
  );
}


/* ==========================================================
   SOUL PICKER POPOVER — quick swap without leaving the chat
   ========================================================== */
function SoulPickerPopover({ onClose, onPick, currentSlug }) {
  const [souls, setSouls] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set('q', q.trim());
        params.set('sort', 'trending');
        params.set('limit', '10');
        const r = await fetch(`${API}/souls?${params.toString()}`);
        if (r.ok) setSouls(await r.json());
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, [q]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="builder-soul-picker"
      className="absolute right-0 top-full mt-2 w-[380px] rounded-2xl border border-white/10 bg-[#0a0a0f]/95 backdrop-blur-2xl shadow-2xl overflow-hidden z-50"
    >
      <div className="p-3 border-b border-white/10 flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a SOUL — coach, yoda, zen…"
            data-testid="builder-soul-picker-search"
            className="w-full h-9 pl-8 pr-3 rounded-lg bg-white/[0.04] border border-white/10 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/25 transition"
            autoFocus
          />
        </div>
        <button
          onClick={onClose}
          className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition"
          title="Close"
        >
          <X size={13} />
        </button>
      </div>

      <div className="max-h-[360px] overflow-y-auto p-2">
        {loading ? (
          <div className="py-8 flex items-center justify-center text-white/50 text-[13px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        ) : souls.length === 0 ? (
          <div className="py-8 text-center text-white/50 text-[13px]">
            No matching personas. <a href="/souls" className="text-cyan-300 hover:text-cyan-200">Publish one?</a>
          </div>
        ) : (
          souls.map((s) => (
            <button
              key={s.slug}
              onClick={() => onPick(s)}
              data-testid={`builder-soul-picker-item-${s.slug}`}
              className={`w-full text-left flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-white/[0.06] transition ${
                s.slug === currentSlug ? 'bg-white/[0.04]' : ''
              }`}
            >
              <div className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-lg" style={{
                background: `linear-gradient(135deg, #00F0FF33 0%, #FF007F33 100%)`,
              }}>
                {s.emoji || (s.name || 'S').slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-semibold text-white truncate">{s.name}</span>
                  {s.slug === currentSlug && (
                    <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-300/30 rounded-full px-1.5 py-0">Active</span>
                  )}
                </div>
                <div className="text-[11px] text-white/55 truncate">{s.tagline}</div>
              </div>
              <div className="text-[10px] text-white/40 shrink-0">
                <span className="inline-flex items-center gap-0.5"><Heart size={9} /> {s.likes}</span>
              </div>
            </button>
          ))
        )}
      </div>

      <div className="border-t border-white/10 p-2.5 flex items-center justify-between text-[11px]">
        <a
          href="/souls"
          data-testid="builder-soul-picker-browse"
          className="text-white/60 hover:text-white flex items-center gap-1 transition"
        >
          Browse full library →
        </a>
        <a
          href="/souls/mine"
          data-testid="builder-soul-picker-mine"
          className="text-white/60 hover:text-white flex items-center gap-1 transition"
        >
          Your souls →
        </a>
      </div>
    </div>
  );
}
